import React, { useRef, useState } from 'react';
import { ImagePlus, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { PhotoUploadData } from '../types/mosque';
import '../styles/PhotoUpload.css';

interface PhotoUploadProps {
  photos: PhotoUploadData[];
  onPhotosChange: (photos: PhotoUploadData[]) => void;
  maxPhotos?: number;
  disabled?: boolean;
}

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const QUALITY_STEPS = [0.9, 0.82, 0.74, 0.66, 0.58, 0.5, 0.42, 0.34, 0.26, 0.2];
const MAX_DIMENSION_STEPS = [4096, 3072, 2560, 2048, 1600, 1280, 1024, 800];

const getExtensionForMime = (mimeType: string): string => {
  if (mimeType === 'image/jpeg') return 'jpg';
  if (mimeType === 'image/png') return 'png';
  return 'webp';
};

const renameWithExtension = (name: string, extension: string): string => {
  const index = name.lastIndexOf('.');
  if (index === -1) {
    return `${name}.${extension}`;
  }
  return `${name.slice(0, index)}.${extension}`;
};

const loadImage = (file: File): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to decode image'));
    };

    image.src = objectUrl;
  });
};

const canvasToBlob = (
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality?: number
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to encode image'));
          return;
        }
        resolve(blob);
      },
      mimeType,
      quality
    );
  });
};

const tryOptimizeImage = async (file: File): Promise<File | null> => {
  if (file.size <= MAX_FILE_SIZE_BYTES) {
    return file;
  }

  const image = await loadImage(file);
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Canvas is unavailable');
  }

  const targetType = file.type === 'image/png' ? 'image/webp' : file.type;
  const extension = getExtensionForMime(targetType);
  const fileName = renameWithExtension(file.name, extension);

  for (const maxDimension of MAX_DIMENSION_STEPS) {
    const largestSide = Math.max(image.width, image.height);
    const scale = Math.min(1, maxDimension / largestSide);
    const width = Math.max(1, Math.round(image.width * scale));
    const height = Math.max(1, Math.round(image.height * scale));
    canvas.width = width;
    canvas.height = height;
    context.clearRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);

    for (const quality of QUALITY_STEPS) {
      const blob = await canvasToBlob(canvas, targetType, quality);

      if (blob.size <= MAX_FILE_SIZE_BYTES) {
        return new File([blob], fileName, {
          type: targetType,
          lastModified: Date.now(),
        });
      }
    }
  }

  return null;
};

const PhotoUpload: React.FC<PhotoUploadProps> = ({
  photos,
  onPhotosChange,
  maxPhotos = 5,
  disabled = false,
}) => {
  const { t } = useLanguage();
  const [error, setError] = useState<string | null>(null);
  const [isPreparing, setIsPreparing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isInteractionDisabled = disabled || isPreparing;

  const handleBrowseClick = () => {
    if (!isInteractionDisabled) {
      fileInputRef.current?.click();
    }
  };

  const handleRemovePhoto = (photoId: string) => {
    const photo = photos.find((item) => item.id === photoId);
    if (photo) {
      URL.revokeObjectURL(photo.previewUrl);
    }

    onPhotosChange(photos.filter((item) => item.id !== photoId));
  };

  const handleCaptionChange = (photoId: string, caption: string) => {
    onPhotosChange(
      photos.map((photo) => (photo.id === photoId ? { ...photo, caption } : photo))
    );
  };

  const addFiles = async (fileList: FileList | null) => {
    if (!fileList || isInteractionDisabled) {
      return;
    }

    setError(null);
    setIsPreparing(true);

    try {
      const files = Array.from(fileList);
      const existingKeys = new Set(photos.map((photo) => `${photo.file.name}-${photo.file.lastModified}-${photo.file.size}`));

      const newPhotos: PhotoUploadData[] = [];
      let firstError: string | null = null;

      for (const file of files) {
        if (!ACCEPTED_TYPES.includes(file.type)) {
          firstError = firstError || t('photos.invalidType');
          continue;
        }

        const key = `${file.name}-${file.lastModified}-${file.size}`;
        if (existingKeys.has(key)) {
          continue;
        }

        let fileToUpload: File | null = file;

        if (file.size > MAX_FILE_SIZE_BYTES) {
          try {
            fileToUpload = await tryOptimizeImage(file);
          } catch {
            firstError = firstError || t('photos.compressionFailed');
            continue;
          }

          if (!fileToUpload) {
            firstError = firstError || t('photos.compressionFailed');
            continue;
          }
        }

        existingKeys.add(key);
        newPhotos.push({
          id: crypto.randomUUID(),
          file: fileToUpload,
          previewUrl: URL.createObjectURL(fileToUpload),
          caption: '',
        });
      }

      if (firstError) {
        setError(firstError);
      }

      if (!newPhotos.length) {
        return;
      }

      const combined = [...photos, ...newPhotos];
      if (combined.length > maxPhotos) {
        combined.slice(maxPhotos).forEach((photo) => URL.revokeObjectURL(photo.previewUrl));
        setError(
          t('photos.maxPhotosReached').replace('{count}', String(maxPhotos))
        );
        onPhotosChange(combined.slice(0, maxPhotos));
        return;
      }

      onPhotosChange(combined);
    } finally {
      setIsPreparing(false);
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(event.target.files).catch(() => {
      setError(t('photos.compressionFailed'));
      setIsPreparing(false);
    });
    event.target.value = '';
  };

  return (
    <div className="photo-upload">
      <div
        className={`photo-dropzone ${isInteractionDisabled ? 'disabled' : ''}`}
        role="button"
        tabIndex={0}
        onClick={handleBrowseClick}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleBrowseClick();
          }
        }}
      >
        <ImagePlus size={20} />
        <p>{isPreparing ? t('photos.compressing') : t('photos.select')}</p>
        <span>{t('photos.maxPhotos').replace('{count}', String(maxPhotos))}</span>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPTED_TYPES.join(',')}
        onChange={handleFileInputChange}
        className="photo-input"
        disabled={isInteractionDisabled}
      />

      {error && <p className="photo-upload-error">{error}</p>}

      {photos.length > 0 && (
        <div className="photo-preview-grid">
          {photos.map((photo) => (
            <div key={photo.id} className="photo-preview-card">
              <img src={photo.previewUrl} alt={photo.file.name} />
              <button
                type="button"
                className="photo-remove-btn"
                onClick={() => handleRemovePhoto(photo.id)}
                disabled={isInteractionDisabled}
                aria-label={t('photos.remove')}
              >
                <X size={14} />
              </button>
              <input
                type="text"
                className="photo-caption-input"
                value={photo.caption || ''}
                onChange={(event) => handleCaptionChange(photo.id, event.target.value)}
                placeholder={t('photos.caption')}
                disabled={isInteractionDisabled}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PhotoUpload;
